// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  first,
  firstValueFrom,
  forkJoin,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  withLatestFrom,
  EMPTY,
  take,
  throwError,
} from 'rxjs';
import { HceAlignmentViewService } from '../../../services/hce/hce-alignment-view/hce-alignment-view.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from '@angular/material/icon';
import { MatSliderModule } from '@angular/material/slider';
import {
  AlignmentViewWasmFacade,
  installEventThrottling,
  uninstallEventThrottling,
  PerfMonitorService,
} from '../../../services/hce/wasm';
import { MatSidenavModule } from '@angular/material/sidenav';
import { Analysis } from '../../../interfaces/analysis';
import { HceLoadingOverlayService } from '../../../services/hce/hce-loading-overlay/hce-loading-overlay.service';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { HceAlignmentViewBody, HceAlignmentViewRequest } from '../../../interfaces/hce/hce-alignment-view';
import { MatDialog } from '@angular/material/dialog';
import { MessageModalComponent } from '../../../shared/message-modal/message-modal.component';
import { HceSvFilter } from '../../../interfaces/hce/hce-sv-filter';
import { HceSvFilterModalComponent, HceSvFilterModalData } from '../hce-sv-filter-modal/hce-sv-filter-modal.component';
import { MatBadgeModule } from '@angular/material/badge';
import { deepEqual } from '../../../services/hce/hce-parser/utils';
import { normalizeHceSvFilter } from '../../../shared/hce/normalizers/hce-sv-filter/hce-sv-filter.normalizer';
import { countHceSvFilter } from '../../../shared/hce/utils/hce-sv-filter/hce-sv-filter.util';

@Component({
  selector: 'app-hce-alignment-view',
  standalone: true,
  imports: [
    CommonModule,
    MatBadgeModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatSidenavModule,
    MatSliderModule,
    ReactiveFormsModule,
  ],
  templateUrl: './hce-alignment-view.component.html',
  styleUrls: ['./hce-alignment-view.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class HceAlignmentViewComponent implements OnInit, AfterViewInit, OnDestroy {
  // ===== View refs =====
  @ViewChild('canvasHost', { static: true }) public hostRef!: ElementRef<HTMLElement>;

  // ===== Public state (used by template) =====
  public params$!: Observable<HceAlignmentViewRequest>;
  public analysis$!: Observable<Analysis>;
  public db$!: Observable<string>;
  public analysis: Analysis | null = null;
  public db = '';
  public isMac = false;
  public isMaplistHidden = false;
  public isMaplistContainerHidden = false;
  public badge = {
    value: 0,
    hidden: true,
  } as { value: number, hidden: boolean };

  public forms = {
    assemble: new FormGroup({
      job_id: new FormControl(''),
      contig: new FormControl(''),
      direction: new FormControl(''),
      cutid: new FormControl(''),
    }),
    alignment: new FormGroup({
      job_id: new FormControl(''),
      db: new FormControl(''),
      bp: new FormControl(''),
      range: new FormControl(''),
      chromosomename: new FormControl(''),
      alignmentscale: new FormControl(''),
      annotation_file: new FormControl(['']),
      contig: new FormControl(''),
    }),
  };

  // ===== Template Getters =====
  public get displayAnalysisId(): number {
    return this.analysis?.AnalysisID ?? 0;
  }
  public get displaySampleName(): string {
    return this.analysis?.SampleName ?? 'back';
  }
  public get summarylink() {
    if (!this.analysis?.AnalysisID) return null;

    const queryParams: any = { tab: 'summary' };

    const files = this.latestParams?.annotationFile;
    if (files && files.length > 0) {
      queryParams.annotations = files;
    }

    return this.router.createUrlTree(
      ['/analysis', 'hce', this.analysis.AnalysisID],
      { queryParams }
    );
  }

  // ===== Private state =====
  private enc$!: Observable<string>;
  private canvas!: HTMLCanvasElement;
  private latestParams: HceAlignmentViewRequest | null = null;
  private svFilter: HceSvFilter = {};
  private readonly listeners: Array<() => void> = [];

  private readonly handlers: any = {
    getAssembleData: async (contigId: number): Promise<string> => {
      // NOTE: Catch error and show modal, then rethrow to propagate to caller (WASM).
      if (!this.analysis) throw new Error('analysis is null');
      try {
        const json = await firstValueFrom(
          this.svc.getAssembleData(this.analysis.AnalysisID, contigId).pipe(
            map(data => JSON.stringify(data)),
          )
        );
        return json;
      } catch (err) {
        this.openErrorModal(err, $localize`Failed to download assembly data.`);
        throw err;
      }
    },
    openUCSC: (chr: string, pos: number) => {
      this.svc.openUcscGenomeBrowser(this.db, chr, pos);
    },
    unblock: () => {
      this.blocker.close();
    },
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly destroyRef: DestroyRef,
    private readonly zone: NgZone,
    private readonly cd: ChangeDetectorRef,
    private readonly svc: HceAlignmentViewService,
    private readonly wasm: AlignmentViewWasmFacade,
    private readonly blocker: HceLoadingOverlayService,
    @Inject(DOCUMENT) private readonly doc: Document,
    private readonly perf: PerfMonitorService,
    private readonly dialog: MatDialog,
  ) {}

  // ===== Lifecycle =====
  public ngOnInit(): void {
    this.doc.body.classList.add('alignment-view');

    const ua = navigator.userAgent.toLowerCase();
    const plat = (navigator.platform || '').toLowerCase();
    this.isMac = plat.includes('mac') || ua.includes('mac os x');

    this.restoreSvFilterFromSession();

    this.enc$ = this.route.paramMap.pipe(
      map(params => params.get('enc')),
      filter((enc): enc is string => !!enc),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    );

    this.params$ = this.enc$.pipe(
      switchMap(enc => this.svc.getParams(enc)),
      tap(params => this.latestParams = params),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.analysis$ = this.params$.pipe(
      map(({ analysisId }) => Number(analysisId)),
      switchMap(analysisId => this.svc.getAnalysis(analysisId)),
      tap(analysis => this.analysis = analysis),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.db$ = this.analysis$.pipe(
      switchMap(analysis => this.svc.getUcscRefVersion(analysis.PanelID)),
      tap(db => this.db = db),
      tap(db => this.forms.alignment.patchValue({ db })),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    // keep warm (avoid unhandled error by catching here; modal + terminate this subscription)
    this.analysis$.pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(err => {
        this.openErrorModal(err, $localize`Failed to load analysis.`);
        return EMPTY;
      })
    ).subscribe();

    this.db$.pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(err => {
        this.openErrorModal(err, $localize`Failed to load genome reference information.`);
        return EMPTY;
      })
    ).subscribe();
  }

  public async ngAfterViewInit(): Promise<void> {
    this.createAndMountCanvas();
    await this.waitForCanvasReady('#main_canvas');

    installEventThrottling();

    // Ensure header DOM exists (params + analysis first emission)
    await firstValueFrom(combineLatest([this.params$, this.analysis$]).pipe(first()));
    this.cd.detectChanges();
    await new Promise<void>(r => requestAnimationFrame(() => r()));
    this.cd.detach();

    // Bridge programmatic form.submit() -> (submit) event
    this.installSubmitEventBridge();

    this.blocker.open($localize`Preparing...`);
    this.blocker.resetTasks();
    this.blocker.startTask({
      id: 'annotation',
      label: $localize`Downloading annotation data...`,
    });
    this.blocker.startTask({
      id: 'alignment',
      label: $localize`Downloading alignment data...`,
    });
    this.blocker.startTask({
      id: 'sv',
      label: $localize`Downloading sv data...`,
    });

    this.perf.start({
      fps: true,
      longTask: true,
      logToConsole: false,
      paint: false,
    });

    // Re-init WASM when id changes, with latest params/analysis
    this.buildInitStream().subscribe({
      // Show error modal on any stream error
      error: (err: any) => {
        try { this.blocker.close(); } catch {}
        this.openErrorModal(err, $localize`Failed to initialize the view.`);
      },
    });
  }

  public ngOnDestroy(): void {
    try {
      this.listeners.forEach(off => off());
    } catch (e) {
      console.warn('[alignment-view] failed to remove some listeners:', e);
    }

    try {
      this.uninstallSubmitEventBridge();
    } catch (e) {
      console.warn('[alignment-view] uninstallSubmitEventBridge failed:', e);
    }

    try {
      this.blocker.close();
    } catch {}
    
    try {
      this.doc.body.classList.remove('alignment-view');
    } catch {}

    try { uninstallEventThrottling(); } catch {}
    try { this.perf.stop(); } catch {}
  }

  // ===== Data fetch / WASM init helpers =====
  private async yieldToBrowser(): Promise<void> {
    // Let the browser paint at least one frame
    await new Promise<void>(r => requestAnimationFrame(() => r()));
    // Then yield one more macrotask to avoid being coalesced with heavy work
    await new Promise<void>(r => setTimeout(r, 0));
  }

  private buildInitStream() {
    return this.enc$.pipe(
      // dispose previous instance first
      switchMap(enc =>
        from(this.wasm.dispose()).pipe(
          catchError(() => of(void 0)),
          map(() => enc)
        )
      ),
      // bind latest params for this id
      withLatestFrom(this.params$),
      tap(([_, params]) => this.patchForms(params)),
      // start wasm
      switchMap(([_, params]) =>
        from(
          this.wasm.prepare({
            job_id: String(params.analysisId),
            range: String(params.range ?? ''),
            bp: String(params.bp ?? ''),
            contig: String(params.contig ?? ''),
            svid: String(params.svId ?? ''),
            unblock: () => {
              this.blocker.close();
            },
            openUCSC: (chr: string, pos: number) => console.log('openUCSC', chr, pos),
          })
        ).pipe(map(() => params))
      ),

      tap(() => this.wasm.registerCallback(this.handlers)),
      switchMap(params =>
        from(this.wasm.waitForExports(['dataInit', 'readSVjson'])).pipe(map(() => params))
      ),
      withLatestFrom(this.analysis$),
      switchMap(([params, analysis]) =>
        of(null).pipe(
          tap(() => this.blocker.setTitle?.($localize`Downloading data...`)),
          switchMap(() => this.getAnnotationData(params, analysis)),
          switchMap(() =>
            forkJoin({
              al: this.getAlignmentData(params, analysis),
              sv: this.getSvData(analysis),
            }).pipe(
              switchMap(({ al, sv }) => {
                if (!al || !sv) {
                  return throwError(() => new Error('Failed to load alignment or sv data'));
                }
                return of({ al, sv });
              })
            )
          ),
          tap(() => {
            this.blocker.setTitle?.($localize`Processing...`);
          }),
          switchMap(v => from(this.yieldToBrowser()).pipe(
            map(() => v)
          )),
          switchMap(({ al, sv }) => new Observable<void>(subscriber => {
            this.zone.runOutsideAngular(async () => {
              try {
                this.blocker.forceCompleteTasks?.(['annotation', 'alignment', 'sv']);
                await this.blocker.flush(2);
                if (al) {
                  this.wasm.callDataInit(JSON.stringify(al));
                }
                if (sv) {
                  this.wasm.callReadSVjson(JSON.stringify(sv), true);
                }
                subscriber.next();
                subscriber.complete();
              } catch (e) {
                subscriber.error(e);
              }
            });
          }))
        )
      ),
      takeUntilDestroyed(this.destroyRef),
      finalize(async () => {
        // Release resources without touching body class here.
        try { await this.wasm.dispose(); } catch {}
        try { this.blocker.close(); } catch {}
      })
    );
  }

  private patchForms(params: HceAlignmentViewRequest) {
    this.forms.assemble.patchValue({
      job_id: String(params.analysisId ?? ''),
      contig: '',
      direction: '',
      cutid: '',
    });
    this.forms.alignment.patchValue({
      job_id: String(params.analysisId ?? ''),
      bp: String(params.bp ?? ''),
      range: String(params.range ?? ''),
      chromosomename: params.chromosomeName ?? '',
      alignmentscale: params.alignmentScale ?? '',
      annotation_file: params.annotationFile ?? [''],
      contig: '',
    });
  }

  private getAnnotationData(params: HceAlignmentViewRequest, analysis: Analysis): Observable<any> {
    return this.svc.getAnnotationData(
      params,
      analysis,
    ).pipe(
      tap(() => this.blocker.completeTask?.('annotation')),
      catchError(err => {
        console.error('Failed to load annotation data.', err);
        this.blocker.errorTask?.('annotation');
        throw err;
      }),
    );
  }

  private getAlignmentData(params: HceAlignmentViewRequest, analysis: Analysis): Observable<any> {
    return this.svc.getAlignmentData(
      analysis,
      params.chromosomeName ?? '',
      params.contig ? Number(params.contig) : null,
    ).pipe(
      tap(() => this.blocker.completeTask?.('alignment')),
      catchError(err => {
        console.error('Failed to load alignment data.', err);
        this.blocker.errorTask?.('alignment');
        return of(null);
      }),
    );
  }

  private getSvData(analysis: Analysis): Observable<any> {
    return this.svc.getSvData(analysis, this.svFilter).pipe(
      tap(() => this.blocker.completeTask?.('sv')),
      catchError(err => {
        console.error('Failed to load sv data.', err);
        this.blocker.errorTask?.('sv');
        return of(null);
      }),
    );
  }

  private getRequiredSvData(analysis: Analysis): Observable<any> {
    return this.getSvData(analysis).pipe(
      switchMap(sv =>
        sv == null
          ? throwError(() => new Error('Failed to load sv data.'))
          : of(sv)
      ),
    );
  }

  private reloadSvWithOverlay(): void {
    if (!this.analysis) return;

    this.blocker.open($localize`Processing...`);
    this.blocker.resetTasks();
    this.blocker.startTask({
      id: 'sv',
      label: $localize`Downloading sv data...`,
    });

    this.getRequiredSvData(this.analysis).pipe(
      take(1),
      switchMap(sv =>
        new Observable<void>(subscriber => {
          this.zone.runOutsideAngular(() => {
            try {
              this.blocker.forceCompleteTasks?.(['sv']);

              this.wasm.callReadSVjson(JSON.stringify(sv), false);

              subscriber.next();
              subscriber.complete();
            } catch (e) {
              subscriber.error(e);
            }
          });
        })
      ),
      catchError(err => {
        this.blocker.errorTask?.('sv');
        this.openErrorModal(err, $localize`Failed to reload sv data.`);
        return EMPTY;
      }),
      finalize(() => {
        // Always close overlay on completion OR error
        this.blocker.close();
      }),
    ).subscribe();
  }

  private createAndMountCanvas() {
    this.zone.runOutsideAngular(() => {
      this.canvas = document.createElement('canvas');
      this.canvas.id = 'main_canvas';
      this.canvas.setAttribute('tabindex', '0');
      this.canvas.style.outline = 'none';
      this.canvas.style.touchAction = 'none';

      // focus on pointer/mouse down so key events reach canvas (WASM keyState)
      const focusCanvas = () => this.canvas.focus({ preventScroll: true });
      const opts: AddEventListenerOptions = { passive: true };
      this.canvas.addEventListener('pointerdown', focusCanvas, opts);
      this.canvas.addEventListener('mousedown', focusCanvas, opts);
      this.listeners.push(() => this.canvas.removeEventListener('pointerdown', focusCanvas, opts));
      this.listeners.push(() => this.canvas.removeEventListener('mousedown', focusCanvas, opts));

      // place before host (same as current)
      this.hostRef.nativeElement.before(this.canvas);
    });
  }

  private async waitForCanvasReady(
    selector = '#main_canvas',
    timeoutMs = 2000
  ): Promise<HTMLCanvasElement> {
    const start = performance.now();

    await new Promise<void>(r => requestAnimationFrame(() => r()));

    let el = this.doc.querySelector<HTMLCanvasElement>(selector);
    while(!(el && el.isConnected)) {
      if (performance.now() - start > timeoutMs) {
        throw new Error(`Canvas (${selector}) not found or not attached to the DOM within ${timeoutMs} ms.`);
      }
      await new Promise<void>(r => setTimeout(r, 16));
      el = this.doc.querySelector<HTMLCanvasElement>(selector);
    }

    const ctx = (el.getContext('2d') || el.getContext('bitmaprenderer'));
    if (!ctx) {
      await new Promise<void>(r => requestAnimationFrame(() => r()));
      if (!(el.getContext('2d') || el.getContext('bitmaprenderer'))) {
        console.warn(
          'Failed to acquire a canvas rendering context (2D/bitmaprenderer). ' +
          'This can happen due to timing. If WASM initializes the context later, it is safe to ignore.'
        );
      }
    }
    return el;
  }

  private runWithChangeDetection(fn: () => void): void {
    this.cd.reattach();
    try {
      fn();
      this.cd.detectChanges();
    } finally {
      this.cd.detach();
    }
  }

  private installSubmitEventBridge(): void {
    const forms = Array.from(this.doc.querySelectorAll('form'));
    for (const form of forms) {
      if ((form as any)._bridgedSubmit) continue;

      (form as any)._originalSubmit = form.submit.bind(form);
      form.submit = () => {
        const ev = new Event('submit', { bubbles: true, cancelable: true });
        form.dispatchEvent(ev);
      };
      (form as any)._bridgedSubmit = true;
    }
  }

  private uninstallSubmitEventBridge(): void {
    const forms = Array.from(this.doc.querySelectorAll('form'));
    for (const form of forms) {
      if (!(form as any)._bridgedSubmit) continue;

      const orig = (form as any)._originalSubmit as HTMLFormElement['submit'] | undefined;
      if (orig) {
        try { form.submit = orig; } catch {}
      }
      delete (form as any)._originalSubmit;
      delete (form as any)._bridgedSubmit;
    }
  }

  // ===== Filter restore / badge helpers =====
  private restoreSvFilterFromSession(): void {
    try {
      const value = sessionStorage.getItem('hceSvFilter');
      const filter = value
        ? normalizeHceSvFilter(JSON.parse(value))
        : normalizeHceSvFilter({});
      this.svFilter = filter;
    } catch {
      this.svFilter = normalizeHceSvFilter({});
    }
    this.runWithChangeDetection(() => {
      this.updateFilterBadge(this.svFilter);
    });
  }

  private applySvFilter(filter: HceSvFilter): void {
    this.svFilter = filter;
    this.runWithChangeDetection(() => {
      this.updateFilterBadge(filter);
    });
  }

  private updateFilterBadge(filter?: HceSvFilter): void {
    if (!filter) {
      this.badge.value = 0;
      this.badge.hidden = true;
      return;
    }

    const count = countHceSvFilter(filter);

    this.badge.value = count;
    this.badge.hidden = count === 0;
  }

  // ===== UI handlers =====
  onAssembleSubmit(e: Event) {
    e.preventDefault();
    if (e.target instanceof HTMLFormElement) {
      const form = e.target;
      this.forms.assemble.patchValue(this.svc.collectFormEntries(form));
      const body = this.forms.assemble.getRawValue();
      const target = form.target;
      this.svc.openAssembleViewer({
        analysisId: String(body.job_id ?? ''),
        contig: String(body.contig ?? ''),
        direction: String(body.direction ?? ''),
        cutId: String(body.cutid ?? ''),
      }, target)
    }
  }

  onAlignmentSubmit(e: Event) {
    e.preventDefault();
    if (e.target instanceof HTMLFormElement) {
      const form = e.target;
      this.forms.alignment.patchValue(this.svc.collectFormEntries(form));
      const body = this.forms.alignment.getRawValue() as unknown as HceAlignmentViewBody;
      const payload = this.svc.mapAlignmentViewToRequest(body);
      const target = form.target;
      this.svc.openAlignmentViewer(payload, target);
    }
  }

  onMaplistToggleClick(e: Event) {
    if (!(e.target instanceof HTMLInputElement)) return;
    const input = e.target;
    this.isMaplistHidden = !input.checked;
    this.cd.reattach();
    try {
      this.cd.detectChanges();
    } finally {
      this.cd.detach();
    }
  }

  onSliderChange(e: Event) {
    if (!(e.target instanceof HTMLInputElement)) return;
    const input = e.target;
    const value = input.valueAsNumber;
    this.wasm.callChangeHideSmall(value);
  }

  onMaplistContainerToggleClick() {
    this.isMaplistContainerHidden = !this.isMaplistContainerHidden;
    this.cd.reattach();
    try {
      this.cd.detectChanges();
    } finally {
      this.cd.detach();
    }
  }
  
  onCopyLinkClick(event: Event) {
    navigator.clipboard.writeText(location.href);
    const balloon = this.doc.getElementById('copyBalloon');
    if (balloon) {
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      balloon.style.left = (rect.right + window.scrollX + 8) + 'px';
      balloon.style.top = (rect.top + window.scrollY) + 'px';
      balloon.style.display = 'block';
      setTimeout(() => {
        balloon.style.display = 'none';
      }, 1200);
    }
  }

  onFilterClick() {
    this.dialog.open<
      HceSvFilterModalComponent,
      HceSvFilterModalData,
      { filter: HceSvFilter; count: number; }
    >(HceSvFilterModalComponent, {
      maxWidth: 'none',
      width: '1200px',
      height: '80vh',
      autoFocus: 'dialog',
      restoreFocus: true,
      data: {
        svFilter: this.svFilter,
        annotationFiles: this.svc.annotationFiles,
        refNameMap: this.svc.refsNameMapping,
      } satisfies HceSvFilterModalData,
    }).afterClosed().subscribe({
      next: (value) => {
        if (!value) return;

        const filter = normalizeHceSvFilter(value.filter);
        if (deepEqual(this.svFilter, filter)) return;

        try {
          sessionStorage.setItem('hceSvFilter', JSON.stringify(filter));
        } catch {}

        this.applySvFilter(filter);
        this.reloadSvWithOverlay();
      }
    });
  }

  // ===== Error modal helper =====
  private errorModalArmed = false;

  private openErrorModal(err: unknown, message = $localize`Unknown error occurred.`): void {
    console.error('[error modal]', err);

    // Prevent stacking multiple dialogs if errors race in
    if (this.errorModalArmed) {
      // Optionally update or re-open message, but avoid adding another afterClosed subscription
      return;
    }
    this.errorModalArmed = true;

    try {
      const ref = this.dialog.open(MessageModalComponent, {
        width: '800px',
        data: {
          title: $localize`Error`,
          message,
        }
      });
      ref.afterClosed()
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.handleFatalCloseOrRedirect());
    } catch {
      // Avoid throwing from the error handler
    }
  }

  private handleFatalCloseOrRedirect(): void {
    try {
      if (window.opener && !window.opener.closed) {
        // In some browsers, window.close() is allowed only for script-opened windows.
        // If the close is blocked, you may still be on this page; fallback below will handle.
        window.close();
        return;
      }
      this.router.navigateByUrl('/').finally(() => {
        try { this.doc.body.classList.remove('alignment-view'); } catch {}
      });
    } finally {
      // Allow re-arming for subsequent errors (e.g., user re-opened this view)
      this.errorModalArmed = false;
    }
  }
}
